import type { PortfolioContent } from "./types";

/**
 * Single source of truth for portfolio copy.
 * Search for TODO_PORTFOLIO to find facts that still need real values.
 */
export const portfolio: PortfolioContent = {
  person: {
    name: "Sam Parhimchik",
    shortName: "Sam",
    brandName: "parhim",
    location: "US citizen · Remote preferred · Open to relocation",
    currentLocation: "Currently based in Miami",
    email: "s@parhim.dev",
    centralMessage:
      "Hands on Engineering Manager and technical leader with senior engineering depth in Solana, fintech, developer infrastructure, and early stage product development.",
    supportingMessage:
      "At Orca I was promoted from Senior Software Engineer to Engineering Manager, leading three engineers and a product designer while remaining involved in architecture, implementation, analytics, and production systems.",
    roleBadge:
      "Senior Software Engineer → Engineering Manager @ Orca · Aug 2024–Jul 2026",
    photoSrc: "/images/me.png",
    photoAlt: "Portrait of Sam Parhimchik",
    resumeHref: "/Sam_Parhimchik_Resume.pdf",
    resumeFilename: "Sam_Parhimchik_Resume.pdf",
    canonicalUrl: "https://parhim.dev/",
  },

  social: [
    {
      id: "github",
      label: "GitHub",
      href: "https://github.com/parhim/",
    },
    {
      id: "linkedin",
      label: "LinkedIn",
      href: "https://www.linkedin.com/in/sam-parhimchik-b4a88a106/",
    },
    {
      id: "email",
      label: "Email",
      href: "mailto:s@parhim.dev",
    },
  ],

  nav: [
    { id: "experience", label: "Experience", href: "#experience" },
    { id: "projects", label: "Projects", href: "#projects" },
    { id: "skills", label: "Skills", href: "#skills" },
    { id: "contact", label: "Contact", href: "#contact" },
  ],

  experienceSection: {
    heading: "Engineering management with technical depth",
    intro:
      "Promoted from Senior Software Engineer to Engineering Manager at Orca. Led a team of three engineers and a product designer while remaining involved in architecture, implementation, and public developer infrastructure.",
  },

  projectsSection: {
    heading: "Flagship case studies",
    intro:
      "Five pieces of work first: transaction infrastructure, team product delivery, partner integration, analytics-led prioritization, and end-to-end product engineering. Additional work lives in the other filters.",
  },

  experience: [
    {
      id: "orca",
      company: "Orca",
      companyUrl: "https://www.orca.so",
      location: "Los Angeles | New York | Miami",
      startDate: "Aug 2024",
      endDate: "Jul 2026",
      roles: ["Senior Software Engineer", "Engineering Manager"],
      progression: {
        from: "Senior Software Engineer",
        to: "Engineering Manager",
        fromStart: "Aug 2024",
        fromEnd: "Sep 2025",
        toStart: "Sep 2025",
        toEnd: "Jul 2026",
      },
      summary:
        "Solana liquidity layer. Promoted from Senior Software Engineer to Engineering Manager. As a senior engineer I led Wavebreak and shipped public transaction tooling; as Engineering Manager I led three engineers and a product designer across Vaults, analytics, and partner integrations while remaining involved in architecture and implementation.",
      featured: true,
      logoSrc: "/images/logos/orca.png",
      teamSize:
        "Managed three engineers · partnered with one product designer",
      impactBullets: [
        "Promoted from Senior Software Engineer (Aug 2024–Sep 2025) to Engineering Manager (Sep 2025–Jul 2026). Managed three engineers and partnered with one product designer.",
        "As Senior Software Engineer, led Wavebreak from research through production — coordinating technical scope, product dependencies, delivery risk, and release sequencing immediately before the promotion.",
        "Authored @orca-so/tx-sender (TypeScript) and contributed to orca_tx_sender (Rust): priority fees, Jito tips, compute estimation, retries, and confirmation. Reached more than 44K weekly npm downloads at peak.",
        "Led the Kamino Vaults partner integration — automated yield strategies live at orca.so/vaults — coordinating Orca-owned product surfaces with external vault infrastructure.",
        "Built Mixpanel instrumentation and funnels for concentrated liquidity deposits, identified abandonment in the deposit path, and used that analysis to change what the team prioritized next.",
      ],
      managementEvidence: {
        title: "Management leverage",
        bullets: [
          "Managed three engineers across Vaults and platform work, setting ownership boundaries, sequencing concurrent releases, and staying involved on high-risk technical paths.",
          "Owned planning and prioritization for the team, balancing roadmap commitments, partner dependencies, unplanned production work, and technical risk with one product designer.",
          "Ran hiring and interviewing for engineering roles on the team.",
          "Held recurring one-on-ones and performance feedback with three engineers, pairing coaching with design review on the work they owned.",
          "Coordinated delivery risk during the Kamino Vaults partner integration, tracking external dependencies and release sequencing through production.",
          "Introduced LLM-assisted TDD and PR review on the team: structured prompts, CI-run generated tests, and human merge approval. Review comments were treated as input, not as a substitute for engineering judgment.",
        ],
      },
      accordions: [
        {
          id: "team-product",
          title: "Team and product ownership",
          body: [
            "As Engineering Manager I was responsible for getting the team's products delivered: planning, prioritization, technical judgment, partner coordination, and release sequencing. Individual contributors owned feature implementation. The product designer owned interaction and visual design. I stayed close enough to the systems to make technical calls and contribute directly when the risk warranted it.",
            "Wavebreak was the main product I led as Senior Software Engineer, immediately before the promotion — Orca's meme token trading platform — from market research and user interviews through production, including on-demand infrastructure for vanity address generation.",
            "Orca Vaults was a partner-backed product after that: Kamino operated the vault infrastructure; the team shipped Orca's deposit, withdraw, portfolio, and launch surfaces so allocators could use those strategies without leaving the platform.",
          ],
          links: [
            {
              label: "Wavebreak case study",
              href: "#project-wavebreak",
              projectId: "wavebreak",
            },
            {
              label: "Orca Vaults case study",
              href: "#project-orca-kamino-vaults",
              projectId: "orca-kamino-vaults",
            },
          ],
        },
        {
          id: "technical",
          title: "Technical contribution",
          body: [
            "As Senior Software Engineer I designed and shipped public TypeScript transaction infrastructure and contributed to the companion Rust crate, before the promotion.",
          ],
          bullets: [
            "Authored @orca-so/tx-sender (TypeScript): transaction construction, priority fees, Jito tips, compute estimation, signer handling, confirmation, retries, and failure handling",
            "Contributed companion Rust crate orca_tx_sender in the same public monorepo",
            "Integrated the packages into the orca-so/whirlpools SDK ecosystem through a public pull request with iterative review (approximately 36 merged commits)",
          ],
          links: [
            {
              label: "TX Sender case study",
              href: "#project-tx-sender",
              projectId: "tx-sender",
            },
            {
              label: "GitHub: orca-so/tx-sender",
              href: "https://github.com/orca-so/tx-sender",
              analyticsEvent: "sdk_github_click",
            },
            {
              label: "TypeScript source",
              href: "https://github.com/orca-so/tx-sender/tree/main/ts",
              analyticsEvent: "sdk_github_click",
            },
            {
              label: "npm: @orca-so/tx-sender",
              href: "https://www.npmjs.com/package/@orca-so/tx-sender",
              analyticsEvent: "sdk_npm_click",
            },
            {
              label: "crates.io: orca_tx_sender",
              href: "https://crates.io/crates/orca_tx_sender",
              analyticsEvent: "sdk_crates_click",
            },
            {
              label: "Whirlpools PR #667",
              href: "https://github.com/orca-so/whirlpools/pull/667",
              analyticsEvent: "sdk_whirlpools_pr_click",
            },
          ],
        },
        {
          id: "analytics",
          title: "Analytics and product decisions",
          body: [
            "I instrumented concentrated liquidity deposit journeys in Mixpanel and built funnels and dashboards so the team could see where users abandoned the flow. The analysis changed what we built next: retention work on the deposit path took priority over adding more surface area.",
          ],
          links: [
            {
              label: "Retention analytics case study",
              href: "#project-orca-liquidity-retention",
              projectId: "orca-liquidity-retention",
            },
          ],
        },
      ],
    },
    {
      id: "openhaus",
      company: "OpenHaus",
      companyUrl: "https://openhaus.ca",
      location: "Vancouver, BC",
      startDate: "Jan 2022",
      endDate: "2024",
      roles: ["Co-founder · Technical Founder · Part time"],
      summary:
        "Commission-free real estate marketplace I co-founded. I designed and implemented the product and infrastructure end to end — application architecture, APIs, frontend, AWS deployment, realtime systems, identity, storage, and offer workflows. The product has been sunset.",
      featured: false,
      spotlight: true,
      logoSrc: "/images/logos/openhaus.png",
      highlights: [
        "Designed and implemented the product and infrastructure: React client, Node.js APIs, PostgreSQL, Dockerized services on AWS EC2, S3 storage, email, SSO, KYC, maps, and Socket.IO realtime updates",
        "Reached approximately $25M in aggregate value of active property listings at peak",
        "Designed OfferFlow after conversations with realtors and people who had purchased multiple properties, then implemented offer creation, negotiation, and contract generation",
        "Shipped a paid listing-promotion plan for real estate developers so their listings appeared above ordinary listings, and operated it for a small set of paying customers",
        "Built an API release path that started the new version on a different port, validated it, then switched traffic — so API releases did not require downtime",
      ],
      technologies: [
        "React",
        "Recoil",
        "Node.js",
        "Express",
        "PostgreSQL",
        "Socket.IO",
        "Docker",
        "AWS EC2",
        "S3",
        "Stripe",
        "Mapbox",
      ],
    },
    {
      id: "psyfi",
      company: "PsyFi",
      location: "Los Angeles, CA",
      startDate: "Mar 2022",
      endDate: "Aug 2024",
      roles: ["Senior Full Stack Engineer"],
      summary:
        "Core product engineer for PsyFi and the PsyOptions protocol — built the primary surfaces for options trading, token launches, liquidity management, airdrops, staking, and mobile lending across the Solana DeFi stack.",
      featured: false,
      logoSrc: "/images/logos/psy.png",
      highlights: [
        "Built and shipped core PsyOptions trading experiences — American-style options on Solana including minting, collateral management, and order flow UX",
        "Designed and built Fusion — collateralized options airdrops using Merkle trees for claim verification at protocol scale",
        "Led frontend development of Armada — LBP curve auctions for token launches and CLMM vaults built on Orca Whirlpools; CLMM vaults reached over $1M TVL without marketing",
        "Built a white-label staking frontend used by Solana teams including Bonk; partner deployments reached approximately $25M in staked assets",
        "Led DeMUX — tokenomics revenue allocation tooling used by multiple Solana teams",
        "Ported PsyLend borrow/lend flows to React Native days after Solana Mobile launch (Pyth / Switchboard oracles, DOV collateral)",
      ],
      technologies: [
        "TypeScript",
        "React",
        "Next.js",
        "React Native",
        "Rust",
        "Anchor",
        "Solana",
        "PsyOptions",
        "Orca Whirlpools",
      ],
    },
    {
      id: "ontraccr",
      company: "OnTraccr",
      location: "San Francisco, CA",
      startDate: "Aug 2020",
      endDate: "Mar 2022",
      roles: ["Full Stack Engineer"],
      summary:
        "First engineer on a SaaS platform for construction contractors — labor tracking, forecasting, data sharing, and automated workflows.",
      featured: false,
      logoSrc: "/images/logos/ontraccr.png",
      highlights: [
        "Built timecard management with dynamic OT and pay calculation",
        "Designed and implemented end-to-end multi-stage timecard approval plus budget and payroll analysis — core product features",
        "Developed core React Native mobile features including dynamic forms, guides, and document center",
      ],
      technologies: [
        "React",
        "Redux",
        "React Native",
        "Node.js",
        "Hapi",
        "MySQL",
        "AWS",
        "Socket.io",
      ],
    },
    {
      id: "atlas-one",
      company: "Atlas One",
      location: "San Francisco, CA",
      startDate: "Oct 2020",
      endDate: "Jan 2021",
      roles: ["Full Stack Engineer (Contract)"],
      summary:
        "Research portal for asset-backed digital securities with live pricing and a composite index.",
      featured: false,
      logoSrc: "/images/logos/atlas.webp",
      highlights: [
        "Designed and developed the research portal for digital securities offerings",
        "Built cron jobs that pull live price data from issuers and calculate an index from historical token price changes",
      ],
      technologies: [
        "React",
        "Redux",
        "Node.js",
        "Express",
        "Keystone",
        "PostgreSQL",
        "GraphQL",
        "CircleCI",
      ],
    },
    {
      id: "wizehire",
      company: "WizeHire",
      location: "San Francisco, CA",
      startDate: "May 2020",
      endDate: "Aug 2020",
      roles: ["Software Engineer (Contract)"],
      summary:
        "Hiring platform work spanning customer support workflows and automated engineer onboarding infrastructure.",
      featured: false,
      logoSrc: "/images/logos/wize.png",
      highlights: [
        "Built job-posting management components that sped up customer and support workflows",
        "Automated VM development stack — reduced new-engineer environment setup from about a week to a few minutes",
      ],
      technologies: ["React", "Django", "PHP", "Jenkins", "GCP", "Bash"],
    },
    {
      id: "philips",
      company: "Philips",
      location: "Vancouver, BC",
      startDate: "May 2019",
      endDate: "May 2020",
      roles: ["Software Developer"],
      summary:
        "Distributed smart senior-home system — IoT device integration, event handling, and facility monitoring dashboards.",
      featured: false,
      logoSrc: "/images/logos/philips.png",
      highlights: [
        "Integrated third-party IoT devices and built apps to manage them and handle events",
        "Built responsive dashboard components for monitoring device status across facilities",
        "Mentored interns and new hires in a team of about ten while helping scale the product",
      ],
      technologies: [
        "Node.js",
        "Angular",
        "TypeScript",
        "Python",
        "Kubernetes",
        "RabbitMQ",
        "MySQL",
        "DynamoDB",
        "AWS S3",
      ],
    },
    {
      id: "terramera",
      company: "Terramera",
      location: "Vancouver, BC",
      startDate: "Sep 2017",
      endDate: "Aug 2018",
      roles: ["Software Engineer"],
      summary:
        "AgTech engineering across data pipelines, internal analytics dashboards, plant imaging hardware software, and computer vision.",
      featured: false,
      logoSrc: "/images/logos/terramera.png",
      highlights: [
        "Built cron-activated inventory download jobs across major retailers into PostgreSQL, saving hours of weekly manual work for sales analysts",
        "Developed statistical and administrative tools for an internal dashboard used by sales and chemistry research teams",
        "Trained a stained-egg segmentation model with 96% accuracy",
      ],
      technologies: [
        "Vue.js",
        "D3.js",
        "Java",
        "PostgreSQL",
        "Docker",
        "Python",
        "Keras",
        "OpenCV",
        "MQTT",
      ],
    },
    {
      id: "iba",
      company: "IBA Group",
      location: "Minsk, Belarus",
      startDate: "Jul 2016",
      endDate: "Aug 2016",
      roles: ["Java Developer (Summer Internship)"],
      summary:
        "Android app for a supermarket network that scans passports via OCR to fill rewards documents.",
      featured: false,
      logoSrc: "/images/logos/iba.png",
      highlights: [
        "Built Android OCR flow using Google Vision and Tesseract for passport scanning",
      ],
      technologies: ["Java", "Android", "Maven"],
    },
  ],

  projects: [
    {
      id: "tx-sender",
      name: "Orca TX Sender",
      role: "Author, TypeScript package · contributor, Rust crate",
      result: "Reached more than 44K weekly npm downloads at peak",
      blurb:
        "Designed Orca's TypeScript transaction sending infrastructure — priority fees, Jito, compute estimation, retries, and confirmation — and landed it through public review.",
      summary:
        "Public TypeScript and Rust packages for building, signing, and landing Solana transactions. I authored @orca-so/tx-sender and contributed to orca_tx_sender, then integrated both into the Whirlpools SDK ecosystem.",
      problem:
        "Solana applications reconstruct the same submission path on every product: assemble instructions, pick compute budget, attach priority fees, optionally tip Jito, choose signers, send, poll for confirmation, and retry when the network drops the transaction. Getting any of those wrong shows up as failed user actions, overpaid fees, or silent stalls. Application teams were reimplementing that path with inconsistent defaults.",
      contribution:
        "I designed and implemented the TypeScript package (@orca-so/tx-sender), contributed to the companion Rust crate (orca_tx_sender), wrote documentation and tests, and took the integration through public review in orca-so/whirlpools.",
      team:
        "I authored the TypeScript package. The Rust crate was a companion implementation in the same public monorepo; I contributed to it rather than claiming sole authorship. Reviewers on the Whirlpools pull request challenged architecture and correctness before merge.",
      constraints: [
        "The API had to be small enough that product teams would actually adopt it — one build-and-send entry point rather than a framework",
        "Fee and tip behavior had to be configurable: dynamic percentiles with caps for premium RPCs, and quieter polling for public RPCs that rate-limit",
        "Confirmation semantics, retries, and Jito versus RPC sending had to be explicit, because silent defaults are how transactions fail in production",
      ],
      decisions: [
        "Center the TypeScript API on setRpc() plus buildAndSendTransaction(), with optional setters for priority fees, Jito tips, and compute-unit margin",
        "Estimate compute, then apply a configurable margin multiplier (default 1.1x) instead of hard-coding a compute budget",
        "Support dual send paths: RPC polling with optional resend, and Jito block engine tips, so callers can trade cost against landing rate",
        "Expose poll interval and resend-on-poll so premium RPCs can poll continuously while public RPCs back off",
      ],
      approach: [
        "Transaction construction from instruction lists, optional address lookup tables, and caller-supplied signers",
        "Dynamic priority fees with a max cap (TypeScript default: 50th percentile, 0.004 SOL cap) and equivalent strategies in Rust including exact, disabled, and percentile modes",
        "Jito tip support via the block engine, with dynamic pricing and a configurable engine URL",
        "Compute-unit estimation plus a margin multiplier so transactions are less likely to exhaust budget without overpaying on every send",
        "Confirmation polling, retry/resend behavior, and failure handling around dropped or unconfirmed transactions",
        "Tests and README-oriented examples so product teams could integrate without reading the implementation",
      ],
      execution: [
        "Shipped the packages as public artifacts on npm and crates.io",
        "Integrated transaction tooling into the Whirlpools SDK through GitHub pull request #667",
        "The Whirlpools review ran across approximately 36 merged commits: reviewers challenged architecture and correctness; I evaluated the feedback, refactored, and added testing where it was warranted, then the change reached approval and production",
      ],
      outcome: [
        "Published @orca-so/tx-sender and orca_tx_sender for public use",
        "Reached more than 44K weekly npm downloads at peak",
        "Merged into Orca's developer tooling surface via the Whirlpools integration",
      ],
      evidence: [
        "npm: @orca-so/tx-sender — https://www.npmjs.com/package/@orca-so/tx-sender",
        "Repository: https://github.com/orca-so/tx-sender",
        "TypeScript source: https://github.com/orca-so/tx-sender/tree/main/ts",
        "Rust crate: https://crates.io/crates/orca_tx_sender",
        "Public review: https://github.com/orca-so/whirlpools/pull/667",
      ],
      extraSections: [
        {
          title: "What the package actually does",
          body: "Callers pass instructions and a signer. The package estimates compute, attaches a compute-budget margin, applies the configured priority-fee and Jito-tip strategies, builds the transaction, sends it over RPC and/or Jito, and waits for confirmation according to the poll settings. The TypeScript surface is deliberately small: configure once, then call buildAndSendTransaction().",
        },
        {
          title: "Review as part of the work",
          body: "The Whirlpools pull request was a long, public review rather than a single-commit merge. Reviewers pushed on architecture and correctness. I treated that as the expected path for a system other teams would depend on: evaluate the feedback, refactor or add tests where it improved the design, and keep iterating until the implementation was approved.",
        },
      ],
      technologies: ["TypeScript", "Rust", "Solana Kit", "Solana", "Jito"],
      links: [
        {
          label: "GitHub repository",
          href: "https://github.com/orca-so/tx-sender",
        },
        {
          label: "TypeScript source",
          href: "https://github.com/orca-so/tx-sender/tree/main/ts",
        },
        {
          label: "npm package",
          href: "https://www.npmjs.com/package/@orca-so/tx-sender",
        },
        {
          label: "Rust crate",
          href: "https://crates.io/crates/orca_tx_sender",
        },
        {
          label: "Whirlpools PR #667",
          href: "https://github.com/orca-so/whirlpools/pull/667",
        },
      ],
      diagram: "tx-sender",
      imageAlt:
        "Diagram of TX Sender: an app submits instructions, the SDK estimates compute and attaches priority fees and Jito tips, then sends over RPC and Jito until the transaction lands on Solana",
      weight: "primary",
      status: "live",
      relatedCompany: "Orca",
    },
    {
      id: "wavebreak",
      name: "Wavebreak",
      role: "Senior Software Engineer · product delivery",
      result: "Shipped from research through production",
      blurb:
        "Led planning, prioritization, and delivery of Orca's token launchpad as a senior engineer — immediately before promotion to Engineering Manager — including technical risk on vanity-address infrastructure.",
      summary:
        "Orca's meme token trading platform. I led delivery as a Senior Software Engineer — research, prioritization, execution, cross-functional sequencing, and the technical calls that affected launch risk — immediately before promotion to Engineering Manager.",
      problem:
        "Meme token trading moves on market time. The team needed a product that reflected how people actually traded, without letting research, design, infrastructure, and launch slip independently. Vanity address generation added a cost and operations constraint: GPU grinding is expensive if it runs continuously.",
      contribution:
        "I owned planning, prioritization, delivery risk, and technical judgment for Wavebreak as a Senior Software Engineer. I coordinated engineering scope, product dependencies, and release sequencing through production. I did not implement every feature; other engineers owned implementation, and the designer owned product design.",
      team:
        "Worked with three engineers and one product designer. I led planning and sequencing, partnered with design on scope, and stayed close enough to infrastructure decisions — including on-demand vanity key generation — to judge technical risk.",
      constraints: [
        "Launch timing depended on market conditions, not only on internal roadmap dates",
        "Vanity address grinding required CUDA on GPU instances; leaving that compute up continuously would have been an unnecessary cost",
        "Scope had to stay inside what three engineers and one designer could ship without splitting into parallel products",
      ],
      decisions: [
        "Grounded feature priority in market research, InsightsStream market intelligence, and direct user interviews rather than building a kitchen-sink trading surface first",
        "Sequenced launch around validated flows instead of waiting for every adjacent idea to land",
        "Ran vanity key grinding as an on-demand AWS GPU job so expensive compute started only when a key was needed",
      ],
      approach: [
        "Planning and execution across engineering and design: ownership boundaries, dependency tracking, and release sequencing",
        "Product decisions informed by market research, trend analysis, InsightsStream, and user interviews",
        "On-demand CUDA vanity-key grinding on AWS GPU instances to keep infrastructure cost tied to actual key generation",
      ],
      execution: [
        "Coordinated discovery through shipping with the three engineers and the product designer",
        "Treated vanity-key infrastructure as a launch risk: scoped the on-demand GPU path so generation did not block the trading surface and did not run idle GPU spend",
        "Held technical and product risk in the same planning cycle — if a dependency slipped, the release sequence changed rather than accumulating hidden work",
      ],
      outcome: [
        "Shipped Wavebreak as a production trading surface at Orca",
        "Automated vanity-key generation that spins up grinding only when needed",
        "Live product documentation at docs.orca.so/campaigns/wavebreak",
      ],
      evidence: [
        "Product docs: https://docs.orca.so/campaigns/wavebreak/overview",
      ],
      technologies: ["TypeScript", "React", "Solana", "CUDA", "AWS"],
      links: [
        {
          label: "Wavebreak docs",
          href: "https://docs.orca.so/campaigns/wavebreak/overview",
        },
      ],
      imageSrc: "/images/projects/wavebreak.png",
      imageAlt:
        "Wavebreak meme token trading platform showing token marketplace and premier launches",
      weight: "primary",
      status: "live",
      relatedCompany: "Orca",
    },
    {
      id: "orca-kamino-vaults",
      name: "Orca Vaults",
      role: "Engineering Manager · partner integration",
      result: "Live at orca.so/vaults",
      blurb:
        "Led delivery of Kamino automated vaults inside Orca — a product that depended on an external partner as much as on our team.",
      summary:
        "Partner integration that surfaces Kamino automated liquidity vaults inside Orca. Allocators can deposit, withdraw, and track positions in the same portfolio as active LP positions, without Orca rebuilding vault infrastructure in-house.",
      problem:
        "Orca served active concentrated-liquidity providers well. Allocators who wanted automated yield still had to leave for a specialist vault product. Rebuilding vault infrastructure would have duplicated a system Kamino already operated, and would have put the timeline on Orca's staffing instead of on a partner who already ran the strategies.",
      contribution:
        "I led product and engineering delivery of the integration: partner alignment, scoping, ownership across Orca-owned surfaces, delivery risk, and launch of the Vaults destination. Engineers on the team implemented the Orca application work. Kamino owned the vault infrastructure the product depended on.",
      team:
        "Orca: three engineers and one product designer, with me as engineering manager for delivery. Kamino: external partner operating vault infrastructure. Integration risk sat on the boundary between those two sides.",
      constraints: [
        "Deposit, withdraw, and position state had to match what Kamino actually operated — Orca could not invent vault behavior on the client",
        "Launch depended on partner readiness as well as Orca's UI, portfolio, and transaction paths",
        "Vault positions needed to appear beside existing Whirlpool LP positions so users did not keep a second mental model of 'money on Orca'",
      ],
      decisions: [
        "Partner with Kamino rather than rebuild vault infrastructure in-house",
        "Ship a first-class Vaults destination instead of hiding the integration inside an existing LP flow",
        "Surface vault positions in Portfolio alongside Whirlpool positions so the product read as one allocator surface",
      ],
      approach: [
        "Split ownership explicitly: Kamino operated strategies and vault mechanics; Orca owned discovery, deposit/withdraw/Autoswap flows, portfolio presentation, and the Vaults destination",
        "Tracked partner dependencies in planning the same way as internal ones — a blocked Kamino path delayed Orca's release sequence rather than being hoped away",
        "Integrated single-token Autoswap alongside deposit and withdraw so the flow matched how allocators actually funded positions",
      ],
      execution: [
        "Coordinated technical and product constraints across Orca engineering, design, and Kamino",
        "Sequenced the launch around the surfaces Orca controlled (Vaults page, portfolio, deposit/withdraw) while the partner path was still a live dependency",
        "Shipped the integration as a production destination rather than a prototype behind a feature flag that never left",
      ],
      outcome: [
        "Live at orca.so/vaults",
        "First Orca product built for allocators who are not active LPs",
        "Vault and Whirlpool positions tracked in the same Portfolio view",
      ],
      evidence: [
        "Product: https://www.orca.so/vaults",
        "Docs: https://docs.orca.so/vaults/getting-started",
        "Launch post: https://blog.orca.so/orca-is-becoming-a-platform-for-every-capital-allocator-on-solana/",
      ],
      technologies: ["TypeScript", "React", "Solana", "Kamino"],
      links: [
        {
          label: "Vaults on Orca",
          href: "https://www.orca.so/vaults",
        },
        {
          label: "Vaults docs",
          href: "https://docs.orca.so/vaults/getting-started",
        },
        {
          label: "Launch post",
          href: "https://blog.orca.so/orca-is-becoming-a-platform-for-every-capital-allocator-on-solana/",
        },
      ],
      imageSrc: "/images/projects/orca-vaults.png",
      imageAlt:
        "Orca Vaults page listing Kamino-managed liquidity strategies with APY and TVL",
      weight: "primary",
      status: "live",
      relatedCompany: "Orca",
    },
    {
      id: "orca-liquidity-retention",
      name: "Improving liquidity deposit retention at Orca",
      role: "Engineering Manager · analytics and prioritization",
      result: "Abandonment in CLMM deposits changed what the team built next",
      blurb:
        "Instrumented concentrated liquidity deposits, found where users dropped off, and used that analysis to change product priorities.",
      summary:
        "I instrumented concentrated liquidity deposit journeys and used the resulting funnels to challenge what the team was about to build. The work was a management decision as much as an analytics project: once abandonment in the deposit path was visible, retention work took priority over adding more surface area.",
      problem:
        "The team could see that concentrated liquidity deposits were a core workflow, but not where users abandoned them or which product changes were worth engineering time. Without instrumentation, prioritization defaulted to roadmap intuition.",
      contribution:
        "I defined Mixpanel event instrumentation, built funnels and dashboards, investigated behavioral patterns in the deposit path, and used those findings in planning so the team sequenced retention work ahead of lower-leverage surface area. Engineers and design implemented the product changes; I owned the analysis and the prioritization call.",
      team:
        "Same product team: three engineers and one product designer. I owned instrumentation, analysis, and the planning decision. Implementation of UI and flow changes sat with engineering and design.",
      constraints: [
        "No existing funnel that distinguished 'did not start a deposit' from 'started and abandoned' in concentrated liquidity",
        "Engineering time was finite; every retention change competed with new product surface",
      ],
      decisions: [
        "Instrument the deposit journey before proposing UI changes, so the team argued from observed drop-off rather than from preference",
        "Treat abandonment in the CLMM deposit path as a product priority, not as a later cleanup item",
        "Evaluate the changes after release against the same funnels rather than declaring the work done at ship",
      ],
      approach: [
        "Defined Mixpanel events for concentrated liquidity deposit and related liquidity workflows",
        "Built funnels and dashboards that made abandonment points visible to engineering and design",
        "Used those patterns in planning to sequence retention work on the deposit path",
        "Scoped product changes with engineering and design, then reviewed the same funnels after release",
      ],
      execution: [
        "The question was: where do users leave concentrated liquidity deposits, and is that drop-off large enough to change the roadmap?",
        "The data was Mixpanel events I specified, wired into funnels for the deposit journey",
        "The pattern was abandonment and incomplete completion in the concentrated liquidity deposit path",
        "The decision was to prioritize retention improvements on that path instead of adding more adjacent product surface first",
      ],
      outcome: [
        "Key liquidity journeys were instrumented with Mixpanel dashboards and funnels",
        "Behavioral analysis changed team priorities toward retention work in CLMM deposit flows",
      ],
      evidence: [
        "Internal Mixpanel funnels and dashboards for concentrated liquidity deposit workflows",
        "Roadmap sequencing that followed the abandonment analysis rather than preceding it",
      ],
      technologies: ["Mixpanel", "TypeScript", "React", "Solana"],
      links: [],
      weight: "primary",
      status: "internal",
      relatedCompany: "Orca",
    },
    {
      id: "openhaus",
      name: "OpenHaus",
      role: "Technical founder · product and infrastructure",
      result:
        "Reached approximately $25M in aggregate value of active property listings at peak",
      blurb:
        "Designed and implemented a commission-free real estate marketplace end to end — offers, realtime messaging, identity, storage, payments, and a no-downtime API release path — then sunset the product.",
      summary:
        "Commission-free real estate marketplace I co-founded and built. I implemented the application, APIs, infrastructure, and the offer system after talking with realtors and repeat buyers. The product has been sunset; the marketing site remains as a record of what shipped.",
      problem:
        "Buying and selling a home without a realtor still required offer documents, negotiation, identity checks, scheduling, media, and a way for both sides to see state change without refreshing. Existing tools assumed a brokerage in the middle. The interesting engineering problem was modeling that transaction loop and operating it in production on a small stack.",
      contribution:
        "I designed and implemented the product and infrastructure: application architecture, backend APIs, frontend, AWS deployment, Dockerized services, email, SSO, KYC, maps, S3 storage and access controls, Socket.IO messaging, realtime UI updates, offer workflows, listing workflows, and a paid promotion plan for developers.",
      team:
        "I was the technical founder and implemented the system. Product decisions came from customer conversations I ran with realtors and people who had purchased multiple properties, plus the operational reality of a small paying-developer segment.",
      constraints: [
        "Part-time founder bandwidth — the architecture had to be operable by one engineer",
        "No Kubernetes, ECS, or managed blue/green platform; deployment had to be practical on EC2",
        "Uploaded listing media needed access control; a public bucket was not acceptable",
        "Users negotiating offers could not be asked to reload the page to see the other side's move",
      ],
      decisions: [
        "Model offers as a structured workflow (subjects, counters, signatures, generated contract) after interviewing people who had actually written and signed those documents",
        "Push listing photos to S3 with presigned URLs rather than proxying file bytes through the API",
        "Use Socket.IO both for listing-scoped chat and to trigger API refreshes so application state updated without a manual reload",
        "Release API versions by starting the new process on another port, validating it, then switching traffic — a practical no-downtime path on EC2 rather than an orchestration platform the product did not need",
        "Add a paid promotion plan for real estate developers once it was clear some sellers would pay to appear above ordinary listings",
      ],
      approach: [
        "Customer research → domain model → product design → implementation → production operation, especially on OfferFlow",
        "React client with Recoil; Node.js/Express APIs; PostgreSQL; Dockerized services on AWS EC2",
        "SSO and KYC gates in the transaction loop; Mapbox for listing geography; SES for email; Stripe for paid add-ons",
        "S3 for listing media, with access controls around upload URLs and storage",
        "Socket.IO for chat and for pushing clients to refresh API state when offers, listings, or messages changed",
      ],
      execution: [
        "Shipped listings, viewing calendars, listing-scoped messaging, KYC, and OfferFlow (offer, counter, contract PDF) as a single product",
        "Operated Dockerized API and web services on EC2, including the port-switch release path for API updates",
        "Designed and shipped paid listing promotion for developers on a short cycle, then supported the paying customers who used it",
      ],
      outcome: [
        "Reached approximately $25M in aggregate value of active property listings at peak — listed property value, not transaction volume, revenue, or GMV",
        "Paid developer promotion worked for a small set of motivated paying customers; the customer base stayed limited in size",
        "Product has been sunset. The marketing site remains online as a closed-marketplace record",
      ],
      evidence: [
        "Sunset marketing site with architecture notes: https://openhaus.ca",
        "Peak metric: approximately $25M aggregate value of active listings, not sales completed",
      ],
      extraSections: [
        {
          title: "OfferFlow, from conversation to implementation",
          body: "I talked with realtors and with people who had purchased multiple properties about how offers actually move: subjects, deposits, counters, and what has to be frozen once a party signs. Those conversations became the domain model. I then implemented a browser-side Contract of Purchase and Sale builder with structured offer data, counter-offers, signature constraints, generated PDFs, and a cached Offerland automated value estimate beside the buyer’s number.",
        },
        {
          title: "Paid developer promotion",
          body: "I designed and implemented a paid plan that let real estate developers promote listings above ordinary listings. Paid add-ons also included MLS exposure. The paying base was small. The useful evidence is that the monetization path was identified, built quickly, shipped, and operated for real paying users — not that it became a large revenue line.",
        },
        {
          title: "API releases without downtime",
          body: "On EC2, a new API version started on a different port. After readiness validation, traffic switched to the new version and the previous version was retired. This was not Kubernetes, ECS, or a managed blue/green service. It was a deployment mechanism matched to the product's stage and to a single operator.",
        },
      ],
      architecture: {
        caption:
          "OpenHaus system: a React client talking to a Node API, with identity, realtime, storage, and third-party services around it, deployed as Dockerized processes on EC2.",
        layers: [
          {
            id: "client",
            label: "Client",
            items: [
              "React application — listings, OfferFlow, messaging, scheduling",
              "Marketing site",
            ],
          },
          {
            id: "api",
            label: "API",
            items: [
              "Node.js / Express APIs",
              "Offer, listing, and promotion workflows",
            ],
          },
          {
            id: "identity",
            label: "Identity",
            items: ["Authentication and SSO", "KYC provider"],
          },
          {
            id: "realtime",
            label: "Realtime",
            items: [
              "Socket.IO listing-scoped chat",
              "Socket.IO-triggered API refreshes so users did not reload to see state changes",
            ],
          },
          {
            id: "data",
            label: "Data",
            items: [
              "PostgreSQL",
              "S3 media with access controls on uploads and storage",
            ],
          },
          {
            id: "services",
            label: "Services",
            items: [
              "Email delivery",
              "Map provider",
              "Stripe for paid listing promotion",
            ],
          },
          {
            id: "deploy",
            label: "Deploy",
            items: [
              "Dockerized services on AWS EC2",
              "New API on another port → readiness check → switch traffic → retire previous version",
            ],
          },
        ],
        notes: [
          {
            title: "What this was not",
            body: "Not Kubernetes, ECS, or a managed blue/green platform. The interesting part is that the deployment mechanism matched the stage of the product.",
          },
        ],
      },
      technologies: [
        "React",
        "Recoil",
        "Node.js",
        "Express",
        "PostgreSQL",
        "Socket.IO",
        "Docker",
        "AWS EC2",
        "S3",
        "Stripe",
        "Mapbox",
      ],
      links: [{ label: "Site (sunset)", href: "https://openhaus.ca" }],
      diagram: "openhaus",
      imageSrc: "/images/projects/openhaus.png",
      imageAlt: "openhaus.ca property listing interface",
      weight: "primary",
      status: "sunset",
      statusNote:
        "Product sunset. The marketing site remains online as a closed-marketplace record.",
      relatedCompany: "OpenHaus",
    },
    {
      id: "visacalendar",
      name: "VisaCalendar",
      role: "Solo product",
      result: "Live at visacalendar.parhim.dev",
      blurb:
        "Privacy-first physical-presence tracker for visa caps, residence rules, and citizenship day counts — data stays in the browser.",
      summary:
        "VisaCalendar is a privacy-first web app that tracks physical-presence days for visa stay limits, residence requirements, and citizenship applications. You log stays in a country, then the app totals unique days against rules you choose: a calendar-year cap, a multi-year total (for example Canada’s 1,095 days in 5 years), or a rolling window like Schengen 90/180. Data stays in the browser, with CSV backup so you can move between devices. Built with React and Vite. Live at visacalendar.parhim.dev.",
      problem:
        "Visa stay limits, PR residency, and citizenship applications count physical-presence days differently — calendar-year caps, multi-year totals, rolling windows — and most tools want an account or a spreadsheet.",
      contribution:
        "Designed and built a client-only React app that logs stays, totals unique days against configurable rules, and backs up via CSV so people can move devices without a server.",
      approach: [
        "Presets for common rules: calendar-year caps, Canada 1,095 days in 5 years, Schengen 90/180, US citizenship 913/5 years, and custom limits",
        "Unique-day counting with overlap handling and ranges that split across year boundaries",
        "localStorage persistence plus CSV export/import of periods and settings",
        "No backend — everything stays in the browser until the user exports a backup",
      ],
      outcome: [
        "Live at visacalendar.parhim.dev",
        "One calculator for calendar-year, multi-year, and rolling-window presence rules",
      ],
      technologies: ["React", "Vite", "Bun"],
      links: [
        { label: "Live app", href: "https://visacalendar.parhim.dev" },
      ],
      imageSrc: "/images/projects/visacalendar.png",
      imageAlt:
        "VisaCalendar physical-presence tracker with yearly day counts and browser-only storage",
      weight: "secondary",
      status: "live",
    },
    {
      id: "armada",
      name: "Armada and staking infrastructure",
      role: "Senior Full Stack Engineer · frontend lead",
      result: "CLMM vaults reached over $1M TVL without marketing",
      blurb:
        "Token launch UI, CLMM vaults, and white-label staking used by Solana teams including Bonk.",
      summary:
        "Solana token launch, liquidity management, and white label staking infrastructure — LBP auctions, CLMM vaults, and a staking frontend used by major teams.",
      problem:
        "Teams needed versatile ways to launch tokens, manage concentrated liquidity, and operate staking programs on Solana.",
      contribution:
        "Led frontend development for Armada and built a white label staking frontend used by Solana teams including Bonk.",
      approach: [
        "Web app for LBP curve auctions and active concentrated liquidity management via Orca Whirlpools based vaults",
        "White label staking frontend supporting multiple protocol deployments",
      ],
      outcome: [
        "CLMM vaults reached over $1M TVL without marketing",
        "Staking frontend used by Solana teams including Bonk; partner deployments reached approximately $25M in staked assets",
      ],
      technologies: ["TypeScript", "React", "Rust", "Solana", "Orca Whirlpools"],
      links: [],
      imageSrc: "/images/projects/armada.png",
      imageAlt: "Armada product interface screenshot",
      weight: "secondary",
      status: "deprecated",
      statusNote: "PsyFi shut down front-end and back-end services on May 1, 2025.",
      relatedCompany: "PsyFi",
    },
    {
      id: "psyoptions",
      name: "PsyOptions Trading Platform",
      role: "Senior Full Stack Engineer",
      blurb:
        "Production American-style options trading on Solana — minting, collateral, and order flow.",
      summary:
        "Core product surfaces for American-style options on Solana — minting, collateral, trading flows, and protocol integrations.",
      problem:
        "PsyOptions needed usable, production-grade interfaces for a complex on-chain options protocol — from minting and collateral management to order flow and portfolio views.",
      contribution:
        "Built and shipped primary PsyOptions trading experiences and related protocol integrations across the PsyFi product suite.",
      approach: [
        "Options minting and collateral management UX",
        "Trading and order flow interfaces on Solana",
        "Integration with PsyOptions American-style options protocol",
        "Extended into Fusion airdrops, staking, and lending surfaces across the same stack",
      ],
      outcome: [
        "Production options trading experiences used across the PsyFi ecosystem",
        "Foundation for Fusion airdrops and downstream DeFi products built on PsyOptions",
      ],
      technologies: ["TypeScript", "React", "Next.js", "Rust", "Anchor", "Solana", "PsyOptions"],
      links: [
        { label: "PsyFi app", href: "https://app.psyfi.io/" },
      ],
      weight: "archive",
      status: "deprecated",
      statusNote: "PsyFi shut down May 1, 2025. App links may no longer resolve.",
      relatedCompany: "PsyFi",
    },
    {
      id: "rps",
      name: "Decentralized Rock-Paper-Scissors",
      role: "Solo build",
      result: "Playable at rockps.parhim.dev",
      blurb:
        "Playable on-chain RPS with commit–reveal fairness and a hot-wallet manager.",
      summary:
        "Personal project — trustless Rock-Paper-Scissors on Solana with an Anchor on-chain program, commit–reveal proofs, and a hot-wallet manager.",
      problem:
        "Classic RPS requires trust; on-chain play needs cheating resistance without poor wallet UX.",
      contribution:
        "Solo build of the Solana program (Anchor/Rust) and React front end — commit–reveal fairness, game state on-chain, and hot-wallet UX.",
      approach: [
        "Anchor program for game state and commit–reveal verification",
        "Hashed commitment scheme with random seeds",
        "Reveal phase for verifiable fairness",
        "Hot wallet manager to reduce signing friction",
      ],
      outcome: ["Playable trustless game at rockps.parhim.dev"],
      technologies: ["Rust", "Anchor", "React", "TypeScript", "Solana"],
      links: [{ label: "Play the game", href: "https://rockps.parhim.dev" }],
      imageSrc: "/images/projects/rps.png",
      imageAlt: "Decentralized Rock-Paper-Scissors interface",
      weight: "secondary",
      status: "live",
    },
    {
      id: "fusion",
      name: "Fusion — Decentralized Airdrop Platform",
      role: "Architecture and web application",
      blurb:
        "Merkle-tree airdrops of fully collateralized options — deprecated by PsyFi before shutdown.",
      summary:
        "Mass airdrops of fully collateralized options on Solana with Merkle-tree claim verification.",
      problem:
        "No reliable decentralized platform for distributing options as airdrops, creating inefficiency and centralized control.",
      contribution:
        "Designed the architecture and built the web application for collateralized options airdrops on PsyOptions.",
      approach: [
        "PsyOptions American-style options protocol for minting",
        "Merkle trees for claim handling",
        "First-come, first-served mechanism for limited airdrops",
      ],
      outcome: [
        "Enabled mass airdrops for fully collateralized options on Solana assets",
      ],
      technologies: ["TypeScript", "Next.js", "Rust", "Solana", "PsyOptions"],
      links: [
        {
          label: "Documentation (deprecated)",
          href: "https://docs.psyfi.io/build-with-psyfi/fusion-airdropping-options-deprecated",
        },
      ],
      imageSrc: "/images/projects/fusion1.png",
      imageAlt: "Fusion airdrop platform interface",
      weight: "archive",
      status: "deprecated",
      statusNote: "Marked deprecated in PsyFi docs; platform shut down May 2025.",
      relatedCompany: "PsyFi",
    },
    {
      id: "psylend-mobile",
      name: "PsyLend Mobile",
      role: "Led Android / React Native port",
      blurb:
        "React Native borrow/lend against DOV positions, shipped days after Solana Mobile launched.",
      summary:
        "React Native borrow/lending app for decentralized option vault positions on Solana.",
      problem:
        "Borrow/lending against DOV positions lacked a mobile experience when Solana Mobile launched.",
      contribution:
        "Led Android development and ported the lending front end to React Native days after Solana Mobile release.",
      approach: [
        "Borrow and lend flows with Pyth and Switchboard oracle price data",
        "Reserve ratio maintenance for leveraged DOV positions",
      ],
      outcome: [
        "Enabled mobile liquidity against DOV positions and USDC/SOL lending yield",
      ],
      technologies: ["React Native", "TypeScript", "Solana"],
      links: [
        { label: "Lending app", href: "https://app.psyfi.io/lending" },
      ],
      imageSrc: "/images/projects/psyfimob.png",
      imageAlt: "PsyLend mobile app screens",
      weight: "archive",
      status: "deprecated",
      statusNote: "PsyFi shut down PsyLend and vaults on May 1, 2025.",
      relatedCompany: "PsyFi",
    },
    {
      id: "demux",
      name: "DeMUX",
      role: "Led web application development",
      blurb:
        "Holder-driven tokenomics UI for routing protocol revenue — used by multiple Solana teams.",
      summary:
        "Tokenomics management web app for Solana DeFi protocol revenue allocation.",
      problem:
        "Projects needed flexible tools for holders to allocate protocol revenues toward buybacks, rewards, and related actions.",
      contribution: "Led development of the web application for revenue allocation strategies.",
      approach: [
        "Holder-driven allocation workflows for Solana DeFi protocols",
      ],
      outcome: ["Used by multiple teams building on Solana"],
      technologies: ["TypeScript", "React", "Solana"],
      links: [],
      imageSrc: "/images/projects/demux.png",
      imageAlt: "DeMUX tokenomics management interface",
      weight: "archive",
      status: "deprecated",
      statusNote: "PsyFi shut down May 1, 2025.",
      relatedCompany: "PsyFi",
    },
    {
      id: "ontraccr-timecards",
      name: "Employee Timecard System",
      role: "Designed and implemented",
      blurb:
        "Live timecards with multi-stage approval, OT calc, and payroll export for construction teams.",
      summary:
        "Live timecard submission, multi-stage approval, OT calculation, and payroll export for construction teams.",
      problem:
        "Timecards needed budgeting, role-based approval chains, task notes, and live sync across users.",
      contribution:
        "Designed and implemented the end-to-end approval system and live sync — a core OnTraccr feature set.",
      approach: [
        "Aggregated daily tasks with optional notes into timecards",
        "Dynamic pay periods with anchor days",
        "Submission and approval logging",
        "Socket.io for live updates across sessions",
      ],
      outcome: [
        "Enabled viewing, editing, approving timesheets and exporting payroll with granular data",
      ],
      technologies: ["React", "Node.js", "Socket.io", "MySQL"],
      links: [{ label: "OnTraccr", href: "https://ontraccr.com/" }],
      imageSrc: "/images/projects/ontraccr.png",
      imageAlt: "OnTraccr timecard system interface",
      weight: "archive",
      status: "live",
      relatedCompany: "OnTraccr",
    },
    {
      id: "wize-env",
      name: "Automated Dev Environment VMs",
      role: "Designed and implemented",
      result: "New-engineer setup reduced from about a week to a few minutes",
      blurb:
        "One-click GCP VMs that cut new-engineer setup from about a week to minutes.",
      summary:
        "One click GCP VM provisioning that cut engineer onboarding setup from about a week to minutes.",
      problem:
        "Multi language distributed systems made local setup fragile and time consuming for new hires.",
      contribution:
        "Built the Django API, frontend trigger, and bash provisioning for identical cloud instances.",
      approach: [
        "Templated Google Cloud compute instances",
        "Automated firewall setup from client IP",
        "nginx served SQL dumps for consistent data",
      ],
      outcome: [
        "Onboarding setup reduced from ~1 week to a few minutes",
        "Identical instances for dependency consistency with production",
      ],
      technologies: ["Django", "Bash", "nginx", "GCP"],
      links: [],
      imageSrc: "/images/projects/env.png",
      imageAlt: "Automated development environment setup interface",
      weight: "archive",
      status: "internal",
      relatedCompany: "WizeHire",
    },
    {
      id: "atlas-research",
      name: "Digital Securities Research Dashboard",
      role: "Designed and developed",
      blurb:
        "Research portal and live index for asset-backed digital securities.",
      summary:
        "Unified research portal and live index for asset-backed digital securities.",
      problem:
        "Issuers were fragmented across APIs and niche venues with no vetted research home for investors.",
      contribution:
        "Designed and developed the portal plus pricing/index cron pipelines.",
      approach: [
        "Graphs, analytics, news, and token directory",
        "Cron jobs for live trade data from multiple sources",
        "Running index from historical trading data",
      ],
      outcome: [
        "Subscription access to exports and financials in a single research surface",
      ],
      technologies: ["React", "Node.js", "Keystone", "GraphQL", "PostgreSQL"],
      links: [{ label: "Atlas One", href: "https://atlasone.ca/" }],
      imageSrc: "/images/projects/atlas_one.gif",
      imageAlt: "Atlas One research dashboard",
      weight: "archive",
      status: "internal",
      relatedCompany: "Atlas One",
    },
    {
      id: "wize-templates",
      name: "Job Template Duplication",
      blurb:
        "Support tooling to duplicate job templates and deep-link them.",
      summary:
        "Support tooling to duplicate job templates and deep-link them, saving hours of daily manual work.",
      problem:
        "Support had to manually copy qualifications and descriptions into empty templates.",
      contribution:
        "Shipped Django REST duplication API and React Router deep-linking.",
      approach: [
        "Replicate template structure while reusing shared features to limit DB growth",
        "Deep links for viewing and editing templates",
      ],
      outcome: ["Hours of daily support work saved via duplication and sharing"],
      technologies: ["Django", "React"],
      links: [],
      imageSrc: "/images/projects/template-dup.gif",
      imageAlt: "Job template duplication workflow",
      weight: "archive",
      status: "internal",
      relatedCompany: "WizeHire",
    },
    {
      id: "wize-invite",
      name: "Legacy Invite Workflow Port",
      blurb:
        "Replaced a legacy PHP invite flow so customers stayed in one dashboard.",
      summary:
        "Replaced a legacy PHP invite flow so customers no longer switched dashboards.",
      problem:
        "Candidates under a billing plan could only be invited from a legacy PHP dashboard.",
      contribution:
        "Wrote the Django REST API and React component with validation and error handling.",
      approach: [
        "New API covering the missing workflow",
        "React form with validation to retire legacy switching",
      ],
      outcome: [
        "Reduced product complexity and improved UX by consolidating into the modern dashboard",
      ],
      technologies: ["Django", "React", "PHP"],
      links: [],
      imageSrc: "/images/projects/invite.gif",
      imageAlt: "Candidate invite workflow",
      weight: "archive",
      status: "internal",
      relatedCompany: "WizeHire",
    },
    {
      id: "wize-tsv",
      name: "TSV Candidate Import",
      blurb:
        "Format-tolerant bulk candidate importer for customer support.",
      summary:
        "Format-tolerant TSV importer for bulk candidate onboarding used by customer support.",
      problem:
        "Import formats changed frequently; pipeline breaks blocked customer onboarding.",
      contribution:
        "Built a PHP TSV parser with format validation and auto-detection across sources.",
      approach: [
        "Regex-oriented parsing for large datasets",
        "Arbitrary column support and format validators",
        "Auto-detect formats such as Indeed and Breezy",
        "Documented on Slab",
      ],
      outcome: [
        "Bulk import without code changes for minor format shifts; hours of manual work removed",
      ],
      technologies: ["PHP"],
      links: [],
      weight: "archive",
      status: "internal",
      relatedCompany: "WizeHire",
    },
  ],

  capabilities: [
    {
      id: "product-engineering",
      title: "Product engineering",
      items: [
        "TypeScript",
        "React",
        "Frontend architecture",
        "Backend systems and APIs",
        "Mobile applications",
        "Dashboards",
        "User experience implementation",
      ],
    },
    {
      id: "systems",
      title: "Systems and infrastructure",
      items: [
        "Distributed systems",
        "Cloud infrastructure (AWS, GCP)",
        "Databases (PostgreSQL, MySQL, DynamoDB)",
        "Messaging (RabbitMQ, Socket.io)",
        "Containers and Kubernetes",
        "Deployment and CI/CD",
        "Reliability-minded delivery",
      ],
    },
    {
      id: "solana",
      title: "Solana and decentralized finance",
      items: [
        "Solana transactions and modern client APIs",
        "Public SDKs (@orca-so/tx-sender, orca_tx_sender)",
        "Priority fees, Jito tips, compute estimation, confirmation",
        "Integrations and partner tooling",
        "Liquidity products and CLMM workflows",
        "DeFi application development",
      ],
    },
    {
      id: "analytics",
      title: "Data and product analytics",
      items: [
        "Mixpanel",
        "Event instrumentation",
        "Funnels and cohorts",
        "Retention analysis",
        "Dashboard development",
        "Using product data to change priorities",
      ],
    },
    {
      id: "ai",
      title: "AI tooling in engineering",
      items: [
        "Internal AI-assisted code review",
        "Test scaffolding and TDD with human validation",
        "Model evaluation (reasoning, coding quality, reliability, latency, cost)",
        "Prompt and context control, including how much diff or repository context is sent",
        "Using smaller models when a larger one is unnecessary",
        "Known failure modes: overfit tests, missed races, hallucinated APIs, shallow review",
      ],
    },
    {
      id: "leadership",
      title: "Leadership and product delivery",
      items: [
        "Engineering management (3 engineers + designer)",
        "Planning, prioritization, and roadmap execution",
        "Hiring and interviewing",
        "Coaching and performance feedback",
        "Technical and product risk management",
        "Partner alignment and cross-functional delivery",
        "Remaining involved in architecture and critical implementation",
      ],
    },
  ],

  additionalExperience: [
    "Node.js / Express / Hapi",
    "Next.js",
    "React Native",
    "Redux / Recoil",
    "Vue.js",
    "Angular / AngularJS",
    "Django",
    "PHP",
    "Rust / Anchor",
    "Python / Keras / OpenCV",
    "Java / Android",
    "GraphQL",
    "D3.js",
    "Docker",
    "MQTT",
    "Jenkins / CircleCI / GitHub Actions",
  ],

  aiEngineering: {
    title: "AI-assisted engineering workflows",
    caseStudy: {
      title: "Internal review, TDD, and model evaluation at Orca",
      intro:
        "I treat models as an engineering system: they need evaluation, constraints, feedback loops, and cost discipline. This is not machine learning research, model training, or production inference infrastructure.",
      problem:
        "Product surface and partner deliverables grew faster than headcount. Test writing and pull request review were the bottlenecks, not idea generation.",
      workflowBefore:
        "Engineers wrote tests after implementation. Pull request review used ad hoc checklists and whatever the reviewer remembered about edge cases.",
      contextProvided:
        "Module boundaries, existing test patterns, acceptance criteria, and the diff under review — not the entire repository by default.",
      contextStructure:
        "Repo conventions, related test files, interface contracts, and task constraints bundled into structured prompts. Context size was an explicit choice: enough for the task, not a dump of the tree.",
      modelOutput:
        "Unit test scaffolds, edge-case suggestions, and pull request comments highlighting risk, missing coverage, and consistency issues. Output was never merged unreviewed.",
      testingAndReview:
        "Generated tests run in CI. Engineers review assertions, fixtures, and async behavior before merge. Review comments are checked against the actual diff. I used models to supplement human review, not to replace it.",
      modelSelection:
        "I evaluate new models as they become available and keep one only when it improves reasoning quality, coding effectiveness, reliability, latency, or cost. Difficult tasks escalate to a more capable model; routine scaffolding stays on a smaller or cheaper one. Model choice is intentional, not 'whatever shipped this week'.",
      costDiscipline:
        "Unnecessary tokens are a defect. I reduce context, trim diffs, avoid resending files the model does not need, and prefer a cheaper model when the task does not require a stronger one.",
      failureModes:
        "Tests that overfit implementation details; missed asynchronous race conditions; hallucinated APIs; confident but wrong assumptions about system behavior; review comments that sound complete and miss architectural issues. Those failure modes are why human merge approval stayed mandatory.",
      humanResponsibility:
        "Architecture decisions, merge approval, production behavior, and final judgment on test quality and review findings.",
      result:
        "The team used the workflow for TDD assistance and PR review. Coverage on new features and partner integrations arrived faster; the review bar stayed a human decision. The useful part was the evaluation loop — including knowing when the model was wrong — not the novelty of using an LLM.",
    },
  },

  lookingFor:
    "I am looking for a hands on Engineering Manager, Tech Lead Manager, or Engineering Lead role where I can lead a small product or platform team while remaining involved in architecture, technical decisions, and critical implementation. I am particularly interested in fintech, developer infrastructure, early stage product companies, and blockchain systems. I prefer remote work and am open to relocation within the United States.",

  education: [
    {
      id: "ubc",
      institution: "University of British Columbia",
      institutionUrl: "https://ubc.ca",
      degree: "Bachelor of Science in Computer Science",
      startYear: "2015",
      endYear: "2020",
      awards: ["Outstanding International Student Award"],
      logoSrc: "/images/logos/ubc.webp",
      logoSrcDark: "/images/logos/ubc_white.webp",
    },
  ],
};
